import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Users,
  UserCircle,
  Link as LinkIcon,
  TrendingUp,
  Megaphone,
  Wallet,
  ArrowRight,
  PiggyBank,
  Receipt,
  ClipboardList,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DashboardGeralPage = () => {
  const [mobilizacao, setMobilizacao] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/stats/dashboard/v2`, {
          withCredentials: true,
        });
        if (mounted) setMobilizacao(data);
      } catch (_err) {
        // silent
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const kpis = mobilizacao?.kpis || {};
  const kv = (k) => (loading ? '—' : kpis?.[k]?.value ?? 0);

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-geral-page">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Dashboard Geral</h1>
          <p className="text-muted-foreground mt-2">
            Visão consolidada da campanha — mobilização e financeiro lado a lado.
          </p>
        </div>

        {/* MÓDULO MOBILIZAÇÃO */}
        <section className="space-y-3" data-testid="section-mobilizacao">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-bold uppercase tracking-wide">Mobilização</h2>
            </div>
            <Button asChild variant="ghost" size="sm" data-testid="link-mob-dashboard">
              <Link to="/mobilizacao/dashboard">
                Dashboard completo <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Link>
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card data-testid="kpi-liderancas">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      Lideranças ativas
                    </p>
                    <p className="text-3xl font-bold mt-1">
                      {kv('liderancas_ativas')}
                    </p>
                  </div>
                  <Users className="h-5 w-5 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card data-testid="kpi-apoiadores">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      Apoiadores
                    </p>
                    <p className="text-3xl font-bold mt-1">
                      {kv('apoiadores_cadastrados')}
                    </p>
                  </div>
                  <UserCircle className="h-5 w-5 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card data-testid="kpi-ingressantes">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      Ingressantes
                    </p>
                    <p className="text-3xl font-bold mt-1">
                      {kv('ingressantes')}
                    </p>
                  </div>
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card data-testid="kpi-conversao">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      Conversão
                    </p>
                    <p className="text-3xl font-bold mt-1">
                      {loading ? '—' : `${kpis?.taxa_conversao?.value ?? 0}%`}
                    </p>
                  </div>
                  <LinkIcon className="h-5 w-5 text-primary" />
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* MÓDULO FINANCEIRO */}
        <section className="space-y-3" data-testid="section-financeiro">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-emerald-600" />
              <h2 className="text-lg font-bold uppercase tracking-wide">Financeiro</h2>
            </div>
            <Button asChild variant="ghost" size="sm" data-testid="link-fin-dashboard">
              <Link to="/financeiro/dashboard">
                Dashboard completo <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Link>
            </Button>
          </div>
          <Card className="border-dashed">
            <CardContent className="pt-8 pb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Link
                  to="/financeiro/planejamento"
                  className="group flex items-start gap-3 p-4 rounded-lg border border-border hover:border-emerald-500 hover:bg-emerald-50/40 transition-colors"
                  data-testid="card-fin-planejamento"
                >
                  <PiggyBank className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-sm">Planejamento orçamentário</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Defina orçamento global, períodos, áreas e cidades.
                    </p>
                  </div>
                </Link>
                <Link
                  to="/financeiro/gastos"
                  className="group flex items-start gap-3 p-4 rounded-lg border border-border hover:border-emerald-500 hover:bg-emerald-50/40 transition-colors"
                  data-testid="card-fin-gastos"
                >
                  <Receipt className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-sm">Gastos</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Lance pagamentos e anexe recibos/notas fiscais.
                    </p>
                  </div>
                </Link>
                <Link
                  to="/financeiro/prestacao"
                  className="group flex items-start gap-3 p-4 rounded-lg border border-border hover:border-emerald-500 hover:bg-emerald-50/40 transition-colors"
                  data-testid="card-fin-prestacao"
                >
                  <ClipboardList className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-sm">Prestação de contas</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Relatório consolidado com exportação para Excel.
                    </p>
                  </div>
                </Link>
              </div>
              <p className="text-center text-xs text-muted-foreground mt-6">
                Módulo financeiro em configuração — comece pelo planejamento orçamentário.
              </p>
            </CardContent>
          </Card>
        </section>
      </div>
    </Layout>
  );
};
